import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { openDB, DBSchema, IDBPDatabase } from 'idb';

const DB_LIST_KEY = 'history-db-list';

export interface HistoryRecord {
  dbId?: number;
  editId?: string;
  value: any;
  changedValue?: any;
}

interface HistoryDB extends DBSchema {
  past: {
    key: number;
    value: HistoryRecord;
    indexes: { 'by-editId': string };
  };
  future: {
    key: number;
    value: HistoryRecord;
    indexes: { 'by-editId': string };
  };
}

@Injectable({ providedIn: 'root' })
export class IDBPDatabaseService {
  private _db: IDBPDatabase<HistoryDB> | null = null;

  private readonly _cleared = new Subject<void>();
  readonly cleared$ = this._cleared.asObservable();

  async initialize(): Promise<void> {
    const dbName = this._getDbName();

    this._deleteAllExcept(dbName);

    this._db = await openDB<HistoryDB>(dbName, 2, {
      upgrade(db) {
        // PAST
        if (!db.objectStoreNames.contains('past')) {
          const past = db.createObjectStore('past', {
            keyPath: 'dbId',
            autoIncrement: true,
          });
          past.createIndex('by-editId', 'editId');
        }

        // FUTURE
        if (!db.objectStoreNames.contains('future')) {
          const future = db.createObjectStore('future', {
            keyPath: 'dbId',
            autoIncrement: true,
          });
          future.createIndex('by-editId', 'editId');
        }
      },
    });

    this._registerDb(dbName);
  }

  private _ensureDb(): IDBPDatabase<HistoryDB> {
    if (!this._db) {
      throw new Error('Database not initialized');
    }
    return this._db;
  }

  async add(record: { value: any; editId?: string }): Promise<{
    record: any;
    pastCount: number;
    futureCount: number;
  }> {
    const db = this._ensureDb();

    const tx = db.transaction(['past', 'future'], 'readwrite');

    await tx.objectStore('past').add(record);

    await tx.objectStore('future').clear();

    const pastCount = await tx.objectStore('past').count();

    await tx.done;

    return {
      record: record,
      pastCount,
      futureCount: 0,
    };
  }

  async undo(): Promise<{
    record: any;
    pastCount: number;
    futureCount: number;
  }> {
    const db = this._ensureDb();

    const tx = db.transaction(['past', 'future'], 'readwrite');

    const pastStore = tx.objectStore('past');
    const futureStore = tx.objectStore('future');

    const cursor = await pastStore.openCursor(null, 'prev');

    if (!cursor) {
      await tx.done;
      return { record: undefined, pastCount: 0, futureCount: 0 };
    }

    const last = cursor.value;

    await futureStore.add(last);

    await cursor.delete();

    const pastCount = await pastStore.count();
    const futureCount = await futureStore.count();

    await tx.done;

    return {
      record: last,
      pastCount,
      futureCount,
    };
  }

  async redo(): Promise<{
    record: any;
    pastCount: number;
    futureCount: number;
  }> {
    const db = this._ensureDb();

    const tx = db.transaction(['past', 'future'], 'readwrite');

    const pastStore = tx.objectStore('past');
    const futureStore = tx.objectStore('future');

    const cursor = await futureStore.openCursor(null, 'prev');

    if (!cursor) {
      await tx.done;
      return { record: undefined, pastCount: 0, futureCount: 0 };
    }

    const item = cursor.value;

    await pastStore.add(item);

    await cursor.delete();

    const pastCount = await pastStore.count();
    const futureCount = await futureStore.count();

    await tx.done;

    return {
      record: item,
      pastCount,
      futureCount,
    };
  }

  async deleteByEditId(editId: string): Promise<{
    pastCount: number;
    futureCount: number;
  }> {
    if (!editId) {
      throw new Error('editId must be provided');
    }

    const db = this._ensureDb();

    const tx = db.transaction(['past', 'future'], 'readwrite');

    const deleteFromStore = async (storeName: 'past' | 'future') => {
      const store = tx.objectStore(storeName);
      const index = store.index('by-editId');

      let cursor = await index.openCursor(IDBKeyRange.only(editId));

      while (cursor) {
        await cursor.delete();
        cursor = await cursor.continue();
      }
    };

    await deleteFromStore('past');
    await deleteFromStore('future');

    const pastCount = await tx.objectStore('past').count();
    const futureCount = await tx.objectStore('future').count();

    await tx.done;

    return { pastCount, futureCount };
  }

  async clear(): Promise<{
    record: undefined;
    pastCount: number;
    futureCount: number;
  }> {
    const db = this._ensureDb();

    const tx = db.transaction(['past', 'future'], 'readwrite');

    await tx.objectStore('past').clear();
    await tx.objectStore('future').clear();

    await tx.done;

    this._cleared.next();

    return {
      record: undefined,
      pastCount: 0,
      futureCount: 0,
    };
  }

  private _getDbName(): string {
    const KEY = 'history-db-name';

    let dbName = sessionStorage.getItem(KEY);

    if (!dbName) {
      dbName = `calculations-history-v2_${crypto.randomUUID()}`;
      sessionStorage.setItem(KEY, dbName);
    }

    return dbName;
  }

  private _registerDb(name: string) {
    const list = JSON.parse(localStorage.getItem(DB_LIST_KEY) || '[]');

    list.push(name);

    localStorage.setItem(DB_LIST_KEY, JSON.stringify(list));
  }

  private _deleteAllExcept(currentDb: string) {
    const list: string[] = JSON.parse(localStorage.getItem(DB_LIST_KEY) || '[]');

    const unique = Array.from(new Set(list));

    for (const dbName of unique) {
      if (dbName !== currentDb) {
        indexedDB.deleteDatabase(dbName);
      }
    }

    // keep only current
    localStorage.setItem(DB_LIST_KEY, JSON.stringify([currentDb]));
  }
}
